import { useState, useEffect, useContext, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { accountService } from '../services/accountService';
import Layout from '../components/Layout';
import ConfirmationModal from '../components/ConfirmationModal';
import MaxBadge from '../components/MaxBadge';
import { prepaidTierMoveMessage, seatUsageLabel, tierMoveErrorMessage } from '../utils/licenses';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  effectiveStatus?: string;
  tier?: 'pro' | 'max';
  effectiveTier?: 'pro' | 'max';
  isAdmin: boolean;
  created: number;
}

export default function UserManagementPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useLingui();
  const [users, setUsers] = useState<User[]>([]);
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [elevateConfirm, setElevateConfirm] = useState<{ isOpen: boolean; userId: string | null }>({
    isOpen: false,
    userId: null,
  });
  const [adminConfirm, setAdminConfirm] = useState<{ isOpen: boolean; userId: string | null }>({
    isOpen: false,
    userId: null,
  });
  const [demoteConfirm, setDemoteConfirm] = useState<{ isOpen: boolean; userId: string | null }>({
    isOpen: false,
    userId: null,
  });
  const [tierConfirm, setTierConfirm] = useState<{ isOpen: boolean; userId: string | null; tier: 'pro' | 'max' }>({
    isOpen: false,
    userId: null,
    tier: 'max',
  });
  const [tierSaving, setTierSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const loadData = async () => {
    try {
      const [companyData, usersData] = await Promise.all([
        accountService.getCompany(),
        accountService.getCompanyUsers()
      ]);
      setCompany(companyData);
      setUsers(usersData);
    } catch (err: any) {
      setError(err.response?.data?.error || t`Failed to load data`);
    } finally {
      setLoading(false);
    }
  };

  // "Add license": the server decides the seat's tier (Max on multi-user
  // domains, Pro only for a single individual). The UI never picks Pro here.
  const handleAddLicense = (userId: string) => {
    setElevateConfirm({ isOpen: true, userId });
  };

  const confirmAddLicense = async () => {
    if (!elevateConfirm.userId) return;
    try {
      const result = await accountService.elevateUserToPro(elevateConfirm.userId);
      const seatTier = result?.tier === 'max' ? 'Max' : 'Pro';
      showToast(t`License added (${seatTier} seat)`, 'success');
      await loadData();
      setElevateConfirm({ isOpen: false, userId: null });
    } catch (err: any) {
      setError(err.response?.data?.error || t`Failed to add a license for this user`);
      setElevateConfirm({ isOpen: false, userId: null });
    }
  };

  const handleMakeAdmin = (userId: string) => {
    setAdminConfirm({ isOpen: true, userId });
  };

  const confirmMakeAdmin = async () => {
    if (!adminConfirm.userId) return;
    try {
      await accountService.makeUserAdmin(adminConfirm.userId);
      showToast(t`User made administrator successfully`, 'success');
      await loadData();
      setAdminConfirm({ isOpen: false, userId: null });
    } catch (err: any) {
      setError(err.response?.data?.error || t`Failed to make user admin`);
      setAdminConfirm({ isOpen: false, userId: null });
    }
  };

  const handleDemoteToMember = (userId: string) => {
    setDemoteConfirm({ isOpen: true, userId });
  };

  const handleSetTier = (userId: string, tier: 'pro' | 'max') => {
    setTierConfirm({ isOpen: true, userId, tier });
  };

  const confirmSetTier = async () => {
    if (!tierConfirm.userId || tierSaving) return;
    setTierSaving(true);
    try {
      await accountService.setUserTier(tierConfirm.userId, tierConfirm.tier);
      showToast(
        tierConfirm.tier === 'max' ? t`User upgraded to Max` : t`User moved to Pro`,
        'success'
      );
      setError('');
      await loadData();
    } catch (err: any) {
      setError(tierMoveErrorMessage(err, tierConfirm.tier === 'max' ? 'Max' : 'Pro'));
    } finally {
      setTierSaving(false);
      setTierConfirm({ isOpen: false, userId: null, tier: 'max' });
    }
  };

  /** Tier confirm copy: proration for card-billed companies, seat inventory for prepaid. */
  const tierConfirmMessage = () => {
    const tierLabel = tierConfirm.tier === 'max' ? 'Max' : 'Pro';
    if (company?.prepaid) {
      return prepaidTierMoveMessage(
        tierLabel,
        tierConfirm.tier === 'max' ? company?.licensedSeatsMax : company?.licensedSeatsPro,
        tierConfirm.tier === 'max' ? company?.claimedSeatsMax : company?.claimedSeatsPro,
      );
    }
    if (tierConfirm.tier === 'max') {
      return t`This moves one seat from Pro to Max. The prorated difference for the rest of the current billing period is charged to your payment method immediately. Continue?`;
    }
    return t`This moves one seat from Max to Pro. A prorated credit is applied to your next invoice. Continue?`;
  };

  /**
   * Add-license confirm copy: the seat bills at the domain's tier. Teams (more
   * than one user) run on Max; only a single-user domain seats at Pro. Prepaid
   * domains claim a seat from their block instead of charging the card.
   */
  const addLicenseMessage = () => {
    const domainTier = users.length > 1 ? 'Max' : 'Pro';
    if (company?.prepaid) {
      return t`This assigns this user a prepaid seat at your domain's tier (${domainTier}). If no ${domainTier} seat is free, the request is refused. Continue?`;
    }
    return t`This adds a license for this user, billed at your domain's tier (${domainTier}: teams with more than one user always run on Max). Your payment method is charged for the new seat. Continue?`;
  };

  const confirmDemoteToMember = async () => {
    if (!demoteConfirm.userId) return;
    try {
      await accountService.demoteUserToMember(demoteConfirm.userId);
      showToast(t`User demoted to member successfully`, 'success');
      await loadData();
      setDemoteConfirm({ isOpen: false, userId: null });
    } catch (err: any) {
      setError(err.response?.data?.error || t`Failed to demote user to member`);
      setDemoteConfirm({ isOpen: false, userId: null });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  const currentUser = users.length > 0 ? users.find(u => u.id === user?.id) : null;
  // Sept 2026 pricing: Pro is an individual account. A domain with more than
  // one user runs Max for every user, so "Downgrade to Pro" is hidden there
  // (the server refuses it with 409 PRO_INDIVIDUAL_ONLY anyway).
  const multiUserDomain = users.length > 1;
  if (users.length > 0 && currentUser && !currentUser.isAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4"><Trans>Access Denied</Trans></h2>
          <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}><Trans>You must be an administrator to access this page.</Trans></p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Trans>Go to Dashboard</Trans>
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold"><Trans>User Management</Trans></h1>
            {company && (
              <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                <Trans>Managing users for <span className="font-semibold">{company.name}</span></Trans>
                {company.isPersonal && <span className="text-xs ml-2" style={{ color: 'var(--color-text-muted)' }}><Trans>(Personal Account)</Trans></span>}
              </p>
            )}
            {company?.prepaid && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-medium"
                  style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                >
                  {seatUsageLabel('Pro', company.claimedSeatsPro, company.licensedSeatsPro)}
                </span>
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-medium"
                  style={{
                    border: '1px solid var(--color-max-border)',
                    backgroundColor: 'var(--color-max-bg)',
                    color: 'var(--color-max-text)',
                  }}
                >
                  {seatUsageLabel('Max', company.claimedSeatsMax, company.licensedSeatsMax)}
                </span>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-risk-bg)', border: '1px solid var(--color-risk-text)', color: 'var(--color-risk-text)' }}>
            {error}
          </div>
        )}

        {/* Desktop Table */}
        <div className="hidden md:block rounded-xl shadow-lg overflow-visible" style={{ backgroundColor: 'var(--color-surface)' }}>
          {users.length === 0 ? (
            <div className="p-12 text-center">
              <p style={{ color: 'var(--color-text-secondary)' }}><Trans>No users found.</Trans></p>
            </div>
          ) : (
            <div className="overflow-visible">
              <table className="w-full">
                <thead style={{ backgroundColor: 'var(--color-surface-secondary)', borderBottom: '1px solid var(--color-border)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}><Trans>User</Trans></th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}><Trans>Status</Trans></th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}><Trans>Role</Trans></th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}><Trans>Actions</Trans></th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                  {users.map((u, index) => (
                    <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium">{u.firstName} {u.lastName}</div>
                        <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{u.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        {(u.effectiveStatus || u.status) !== 'pro' ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300">
                            <Trans>Trial</Trans>
                          </span>
                        ) : u.effectiveTier === 'max' ? (
                          <MaxBadge />
                        ) : (
                          <span
                            className="px-2 py-1 text-xs font-semibold rounded-full"
                            style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                          >
                            Pro
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {u.isAdmin ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300">
                            <Trans>Administrator</Trans>
                          </span>
                        ) : (
                          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}><Trans>Member</Trans></span>
                        )}
                      </td>
                      <td className="px-6 py-4 relative" style={{ overflow: 'visible' }}>
                        <div className="relative" ref={(el) => { menuRefs.current[u.id] = el; }}>
                          <button
                            onClick={() => setOpenMenuId(openMenuId === u.id ? null : u.id)}
                            className="p-2 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                            style={{ color: 'var(--color-text-muted)' }}
                            title={t`User actions`}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="1" />
                              <circle cx="12" cy="5" r="1" />
                              <circle cx="12" cy="19" r="1" />
                            </svg>
                          </button>
                          {openMenuId === u.id && (
                            <div
                              className="absolute right-0 rounded-lg shadow-lg py-1"
                              style={{ 
                                backgroundColor: 'var(--color-surface)',
                                border: '1px solid var(--color-border)',
                                minWidth: '180px',
                                ...(index >= users.length - 2 ? { bottom: '100%', marginBottom: '4px' } : { top: '100%', marginTop: '4px' }),
                                zIndex: 1000
                              }}
                            >
                              <button onClick={() => { navigate(`/users/detail/${u.id}`); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-white/5" style={{ color: 'var(--color-text)' }}>
                                <Trans>View User Audit</Trans>
                              </button>
                              {u.status !== 'pro' && !company?.isPersonal && (
                                <button onClick={() => { handleAddLicense(u.id); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-white/5" style={{ color: 'var(--color-text)' }}>
                                  <Trans>Add license</Trans>
                                </button>
                              )}
                              {!company?.isPersonal && (
                                u.tier === 'max' ? (
                                  !multiUserDomain && (
                                    <button onClick={() => { handleSetTier(u.id, 'pro'); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-white/5" style={{ color: 'var(--color-text)' }}>
                                      <Trans>Downgrade to Pro</Trans>
                                    </button>
                                  )
                                ) : (
                                  <button onClick={() => { handleSetTier(u.id, 'max'); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-white/5" style={{ color: 'var(--color-max-text)' }}>
                                    <Trans>Upgrade to Max</Trans>
                                  </button>
                                )
                              )}
                              {!u.isAdmin && !company?.isPersonal && currentUser?.isAdmin && (
                                <button onClick={() => { handleMakeAdmin(u.id); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-white/5" style={{ color: 'var(--color-text)' }}>
                                  <Trans>Make Admin</Trans>
                                </button>
                              )}
                              {u.isAdmin && !company?.isPersonal && currentUser?.isAdmin && u.id !== user?.id && (
                                <button onClick={() => { handleDemoteToMember(u.id); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-white/5" style={{ color: 'var(--color-text)' }}>
                                  <Trans>Demote to Member</Trans>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Card List */}
        <div className="md:hidden space-y-3">
          {users.length === 0 ? (
            <div className="glass-panel rounded-xl p-8 text-center">
              <p style={{ color: 'var(--color-text-secondary)' }}><Trans>No users found.</Trans></p>
            </div>
          ) : (
            users.map((u) => (
              <div key={u.id} className="glass-panel rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{u.firstName} {u.lastName}</p>
                    <p className="text-sm truncate" style={{ color: 'var(--color-text-muted)' }}>{u.email}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 ml-3">
                    {(u.effectiveStatus || u.status) !== 'pro' ? (
                      <span className="px-2 py-1 text-[10px] font-semibold rounded-full bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300">
                        <Trans>Trial</Trans>
                      </span>
                    ) : u.effectiveTier === 'max' ? (
                      <MaxBadge />
                    ) : (
                      <span
                        className="px-2 py-1 text-[10px] font-semibold rounded-full"
                        style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                      >
                        Pro
                      </span>
                    )}
                    {u.isAdmin && (
                      <span className="px-2 py-1 text-[10px] font-semibold rounded-full bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300">
                        <Trans>Admin</Trans>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <button
                    onClick={() => navigate(`/users/detail/${u.id}`)}
                    className="px-3 py-1.5 text-xs rounded-lg transition-colors bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300"
                  >
                    <Trans>View Audit</Trans>
                  </button>
                  {u.status !== 'pro' && !company?.isPersonal && (
                    <button onClick={() => handleAddLicense(u.id)} className="px-3 py-1.5 text-xs rounded-lg transition-colors" style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                      <Trans>Add license</Trans>
                    </button>
                  )}
                  {!company?.isPersonal && (
                    u.tier === 'max' ? (
                      !multiUserDomain && (
                        <button onClick={() => handleSetTier(u.id, 'pro')} className="px-3 py-1.5 text-xs rounded-lg transition-colors" style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                          <Trans>Downgrade to Pro</Trans>
                        </button>
                      )
                    ) : (
                      <button
                        onClick={() => handleSetTier(u.id, 'max')}
                        className="px-3 py-1.5 text-xs rounded-lg transition-colors font-medium"
                        style={{ border: '1px solid var(--color-max-border)', backgroundColor: 'var(--color-max-bg)', color: 'var(--color-max-text)' }}
                      >
                        <Trans>Upgrade to Max</Trans>
                      </button>
                    )
                  )}
                  {!u.isAdmin && !company?.isPersonal && currentUser?.isAdmin && (
                    <button onClick={() => handleMakeAdmin(u.id)} className="px-3 py-1.5 text-xs rounded-lg transition-colors" style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                      <Trans>Make Admin</Trans>
                    </button>
                  )}
                  {u.isAdmin && !company?.isPersonal && currentUser?.isAdmin && u.id !== user?.id && (
                    <button onClick={() => handleDemoteToMember(u.id)} className="px-3 py-1.5 text-xs rounded-lg transition-colors text-red-600 dark:text-red-400" style={{ border: '1px solid var(--color-border)' }}>
                      <Trans>Demote</Trans>
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <ConfirmationModal isOpen={elevateConfirm.isOpen} title={t`Add License`} message={addLicenseMessage()} confirmText={t`Add license`} cancelText={t`Cancel`} onConfirm={confirmAddLicense} onCancel={() => setElevateConfirm({ isOpen: false, userId: null })} variant="default" />
        <ConfirmationModal isOpen={adminConfirm.isOpen} title={t`Make User Administrator`} message={t`This user will have full administrative access. Continue?`} confirmText={t`Continue`} cancelText={t`Cancel`} onConfirm={confirmMakeAdmin} onCancel={() => setAdminConfirm({ isOpen: false, userId: null })} variant="default" />
        <ConfirmationModal isOpen={demoteConfirm.isOpen} title={t`Demote User to Member`} message={t`This user will lose administrative access and become a regular member. Continue?`} confirmText={t`Continue`} cancelText={t`Cancel`} onConfirm={confirmDemoteToMember} onCancel={() => setDemoteConfirm({ isOpen: false, userId: null })} variant="default" />
        <ConfirmationModal
          isOpen={tierConfirm.isOpen}
          title={tierConfirm.tier === 'max' ? t`Upgrade User to Max` : t`Downgrade User to Pro`}
          message={tierConfirmMessage()}
          confirmText={tierConfirm.tier === 'max' ? t`Upgrade to Max` : t`Downgrade to Pro`}
          cancelText={t`Cancel`}
          onConfirm={confirmSetTier}
          onCancel={() => setTierConfirm({ isOpen: false, userId: null, tier: 'max' })}
          variant="default"
        />
      </div>
    </Layout>
  );
}
